import { noddiAsync, URLKeys } from 'noddi-async';
import {
  NavioOverlayActions,
  NoddiButton,
  NoddiCard,
  NoddiDialog,
  NoddiInfoBanner,
  NoddiPasswordStrength,
  useAppForm
} from 'noddi-ui-web';
import { useState } from 'react';
import type { z } from 'zod';
import { useAuthContext } from '../../contexts/useAuthContext';
import { createCurrentPasswordSchema, createNewPasswordSchema } from '../Login/passwordResetSchemas';

type PasswordChangeTranslations = {
  title: string;
  description: string;
  changePassword: string;
  currentPasswordTitle: string;
  currentPasswordDescription: string;
  currentPassword: string;
  continue: string;
  newPasswordTitle: string;
  newPasswordDescription: string;
  newPassword: string;
  confirmPassword: string;
  cancel: string;
  back: string;
  passwordChangedSuccessfully: string;
  done: string;
  currentPasswordRequired: string;
  passwordRequired: string;
  passwordMinLength: string;
  confirmPasswordRequired: string;
  passwordsDontMatch: string;
};

interface PasswordChangeProps {
  translations?: PasswordChangeTranslations;
}

const defaultTexts: PasswordChangeTranslations = {
  title: 'Password',
  description: 'Change the password you use to sign in.',
  changePassword: 'Change password',
  currentPasswordTitle: 'Confirm your password',
  currentPasswordDescription: 'Enter your current password to continue.',
  currentPassword: 'Current password',
  continue: 'Continue',
  newPasswordTitle: 'Choose a new password',
  newPasswordDescription: 'Enter a new password and confirm it below.',
  newPassword: 'New password',
  confirmPassword: 'Confirm new password',
  cancel: 'Cancel',
  back: 'Back',
  passwordChangedSuccessfully: 'Your password has been changed successfully.',
  done: 'Done',
  currentPasswordRequired: 'Current password is required',
  passwordRequired: 'Password is required',
  passwordMinLength: 'Password must be at least 12 characters',
  confirmPasswordRequired: 'Please confirm your password',
  passwordsDontMatch: "Passwords don't match"
};

type Step = 'current' | 'new' | 'success';

const CURRENT_PASSWORD_FORM_ID = 'password-change-current-password-form';
const NEW_PASSWORD_FORM_ID = 'password-change-new-password-form';

export const PasswordChange = ({ translations }: PasswordChangeProps) => {
  const { userData } = useAuthContext();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('current');
  const [currentPassword, setCurrentPassword] = useState('');
  const texts = translations ?? defaultTexts;

  const resetDialog = () => {
    setStep('current');
    setCurrentPassword('');
  };

  const handleClose = () => {
    setOpen(false);
    resetDialog();
  };

  if (!userData?.user.id) {
    return null;
  }

  const dialogTitle =
    step === 'current' ? texts.currentPasswordTitle : step === 'new' ? texts.newPasswordTitle : texts.changePassword;

  const dialogDescription =
    step === 'current' ? texts.currentPasswordDescription : step === 'new' ? texts.newPasswordDescription : undefined;

  return (
    <NoddiCard title={texts.title} description={texts.description}>
      <div className='flex justify-end'>
        <NoddiButton
          type='button'
          onClick={() => {
            resetDialog();
            setOpen(true);
          }}
        >
          {texts.changePassword}
        </NoddiButton>
      </div>

      <NoddiDialog
        open={open}
        onClose={handleClose}
        title={dialogTitle}
        description={dialogDescription}
        maxWidth='sm'
        footer={
          step === 'success' && (
            <NoddiButton type='button' onClick={handleClose} className='w-full'>
              {texts.done}
            </NoddiButton>
          )
        }
      >
        {step === 'current' && (
          <CurrentPasswordStep
            texts={texts}
            initialPassword={currentPassword}
            onContinue={(password) => {
              setCurrentPassword(password);
              setStep('new');
            }}
            onCancel={handleClose}
          />
        )}
        {step === 'new' && (
          <NewPasswordStep
            texts={texts}
            currentPassword={currentPassword}
            onSuccess={() => setStep('success')}
            onBack={() => setStep('current')}
          />
        )}
        {step === 'success' && (
          <NoddiInfoBanner variant='success' texts={[{ value: texts.passwordChangedSuccessfully }]} />
        )}
      </NoddiDialog>
    </NoddiCard>
  );
};

type StepTexts = PasswordChangeTranslations;

type CurrentPasswordStepProps = {
  texts: StepTexts;
  initialPassword: string;
  onContinue: (currentPassword: string) => void;
  onCancel: () => void;
};

const CurrentPasswordStep = ({ texts, initialPassword, onContinue, onCancel }: CurrentPasswordStepProps) => {
  const validationSchema = createCurrentPasswordSchema(texts);
  type FormValues = z.input<typeof validationSchema>;

  const form = useAppForm({
    defaultValues: { currentPassword: initialPassword } satisfies FormValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: ({ value }) => {
      onContinue(value.currentPassword);
    }
  });

  return (
    <form.AppForm>
      <form
        id={CURRENT_PASSWORD_FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className='flex flex-col gap-4'>
          <form.AppField name='currentPassword'>
            {(field) => (
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- first field of the password-change form the user opened
              <field.Input type='password' label={texts.currentPassword} autoComplete='current-password' autoFocus />
            )}
          </form.AppField>
          <NavioOverlayActions>
            <NoddiButton type='button' variant='outline' onClick={onCancel} className='flex-1'>
              {texts.cancel}
            </NoddiButton>
            <form.SubscribeButton formId={CURRENT_PASSWORD_FORM_ID} label={texts.continue} className='flex-1' />
          </NavioOverlayActions>
        </div>
      </form>
    </form.AppForm>
  );
};

type NewPasswordStepProps = {
  texts: StepTexts;
  currentPassword: string;
  onSuccess: () => void;
  onBack: () => void;
};

const NewPasswordStep = ({ texts, currentPassword, onSuccess, onBack }: NewPasswordStepProps) => {
  const { mutateAsync: changePassword } = noddiAsync.usePost({
    type: URLKeys.postPasswordChange
  });

  const validationSchema = createNewPasswordSchema(texts);
  type FormValues = z.input<typeof validationSchema>;

  const form = useAppForm({
    defaultValues: { password: '', confirmPassword: '' } satisfies FormValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      await changePassword({
        currentPassword,
        newPassword: value.password
      });
      onSuccess();
    }
  });

  return (
    <form.AppForm>
      <form
        id={NEW_PASSWORD_FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className='flex flex-col gap-4'>
          <form.AppField name='password'>
            {(field) => (
              <div className='flex flex-col gap-1'>
                {/* oxlint-disable-next-line jsx-a11y/no-autofocus -- first field of the set-password form the user opened */}
                <field.Input type='password' label={texts.newPassword} autoComplete='new-password' autoFocus />
                <NoddiPasswordStrength password={field.state.value} />
              </div>
            )}
          </form.AppField>
          <form.AppField name='confirmPassword'>
            {(field) => <field.Input type='password' label={texts.confirmPassword} autoComplete='new-password' />}
          </form.AppField>
          <NavioOverlayActions>
            <NoddiButton type='button' variant='outline' onClick={onBack} className='flex-1'>
              {texts.back}
            </NoddiButton>
            <form.SubscribeButton formId={NEW_PASSWORD_FORM_ID} label={texts.changePassword} className='flex-1' />
          </NavioOverlayActions>
        </div>
      </form>
    </form.AppForm>
  );
};
